import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useMutation, useQueries, useQueryClient } from "@project/ui";
import { Button } from "@project/ui";
import { Icon, cn } from "@project/ui";
import { useI18n } from "@apps/dashboard/contexts/I18nContext";
import { useActiveHouse } from "@apps/dashboard/features/houses/active-house-context";
import {
  createHouse,
  deleteHouse,
  fetchDashboardSummary,
  homeExpenseQueryKeys,
  updateHouse,
} from "@apps/dashboard/lib/home-expense-api";
import { ApiRequestError } from "@apps/dashboard/lib/api-fetch";
import { formatMinorAsCurrency } from "@apps/dashboard/lib/format-money";
import type { DashboardSummary } from "@apps/dashboard/lib/home-expense-types";
import { dashboardPaths } from "@apps/dashboard/lib/routes";
import { currentYearMonth } from "@apps/dashboard/lib/year-month";
import { DASHBOARD_PAGE_CONTAINER } from "@apps/dashboard/components/layout/dashboard-page-layout";
import { ErrorState } from "@apps/dashboard/components/states/ErrorState";
import { LoadingState } from "@apps/dashboard/components/states/LoadingState";
import { HouseFinancialProfileSection } from "@apps/dashboard/features/houses/house-financial-profile-section";

type BudgetGlanceKind = "neutral" | "within" | "tight" | "over";

function resolveBudgetGlance(s: DashboardSummary): {
  kind: BudgetGlanceKind;
  usagePercent: number | null;
  plannedMinor: number;
  spentMinor: number;
  currency: string;
} {
  const cap = s.monthlyBudgetCapMinor;
  const planned = s.plannedBudgetMinor;
  const denominator =
    cap !== null && cap !== undefined && cap > 0
      ? cap
      : planned > 0
        ? planned
        : null;
  const spent = s.spentMinor;
  const rawPct =
    denominator !== null && denominator > 0 ? (spent / denominator) * 100 : null;
  let kind: BudgetGlanceKind = "neutral";
  if (rawPct != null) {
    if (rawPct > 100) kind = "over";
    else if (rawPct >= 85) kind = "tight";
    else kind = "within";
  }
  return {
    kind,
    usagePercent: rawPct,
    plannedMinor: planned,
    spentMinor: spent,
    currency: s.currency,
  };
}

export default function HousesPage(): JSX.Element {
  const { t, locale } = useI18n();
  const queryClient = useQueryClient();
  const { houses, housesLoading, housesError, refetchHouses, activeHouseId, setActiveHouseId } =
    useActiveHouse();
  const [name, setName] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [financialProfileEditHouseId, setFinancialProfileEditHouseId] = useState<string | null>(null);
  const [nameEditHouseId, setNameEditHouseId] = useState<string | null>(null);
  const [nameEditValue, setNameEditValue] = useState("");

  const createMutation = useMutation({
    mutationFn: (n: string) => createHouse(locale, n),
    onSuccess: () => {
      setName("");
      setFormError(null);
      void queryClient.invalidateQueries({ queryKey: ["houses"] });
    },
    onError: (err: unknown) => {
      const msg = err instanceof ApiRequestError ? err.message : t("dashboardUi.auth.errors.generic");
      setFormError(msg);
    },
  });

  const updateNameMutation = useMutation({
    mutationFn: ({ houseId, nextName }: { houseId: string; nextName: string }) =>
      updateHouse(locale, houseId, { name: nextName }),
    onSuccess: () => {
      setNameEditHouseId(null);
      void queryClient.invalidateQueries({ queryKey: ["houses"] });
      toast.success(t("dashboardUi.houses.updateNameSuccess"));
    },
    onError: (err: unknown) => {
      const msg = err instanceof ApiRequestError ? err.message : t("dashboardUi.houses.updateNameError");
      toast.error(msg);
    },
  });

  const deleteHouseMutation = useMutation({
    mutationFn: (houseId: string) => deleteHouse(locale, houseId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["houses"] });
      toast.success(t("dashboardUi.houses.deleteSuccess"));
    },
    onError: (err: unknown) => {
      const msg = err instanceof ApiRequestError ? err.message : t("dashboardUi.houses.deleteError");
      toast.error(msg);
    },
  });

  const list = houses ?? [];
  const yearMonth = currentYearMonth();
  const summariesEnabled = houses !== undefined && !housesError;

  const houseSummaryQueries = useQueries({
    queries: list.map((h) => ({
      queryKey: homeExpenseQueryKeys.dashboardSummary(locale, h.id, yearMonth),
      queryFn: () => fetchDashboardSummary(locale, h.id, yearMonth),
      enabled: summariesEnabled,
    })),
  });

  if (housesLoading && houses === undefined) {
    return (
      <div className={DASHBOARD_PAGE_CONTAINER}>
        <LoadingState />
      </div>
    );
  }

  if (housesError) {
    return (
      <div className={DASHBOARD_PAGE_CONTAINER}>
        <ErrorState onRetry={() => refetchHouses()} />
      </div>
    );
  }

  return (
    <div className={DASHBOARD_PAGE_CONTAINER}>
      <h1 className="font-headline text-headline-sm font-bold text-primary">{t("dashboardUi.houses.pageTitle")}</h1>

      <section
        className="mt-8 rounded-2xl bg-surface-container-low p-6 outline outline-1 outline-outline-variant/15"
        data-tour="houses-page-create"
      >
        <h2 className="font-headline text-sm font-bold text-on-surface">{t("dashboardUi.houses.createTitle")}</h2>
        <form
          className="mt-4 flex flex-col gap-4 sm:flex-row sm:items-end"
          onSubmit={(e) => {
            e.preventDefault();
            const trimmed = name.trim();
            if (!trimmed) return;
            createMutation.mutate(trimmed);
          }}
        >
          <div className="flex-1">
            <label htmlFor="new-house-name" className="text-label-md text-on-surface-variant">
              {t("dashboardUi.houses.nameLabel")}
            </label>
            <input
              id="new-house-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("dashboardUi.houses.namePlaceholder")}
              className="mt-1 w-full rounded-xl border border-outline-variant/30 bg-surface px-4 py-3 font-body text-body-md text-on-surface outline-none focus:border-primary"
              autoComplete="off"
            />
          </div>
          <Button type="submit" disabled={createMutation.isPending || !name.trim()}>
            {createMutation.isPending ? t("dashboardUi.houses.creating") : t("dashboardUi.houses.createCta")}
          </Button>
        </form>
        {formError ? <p className="mt-3 text-body-md text-error">{formError}</p> : null}
      </section>

      {list.length === 0 ? (
        <div
          className="mt-10 flex min-h-[14rem] flex-col items-center justify-center gap-2 rounded-2xl bg-surface-container-low px-6 py-12 text-center outline outline-1 outline-outline-variant/15"
          data-tour="houses-empty-tour"
        >
          <Icon name="Home" className="size-12 text-on-surface-variant/50" strokeWidth={1.5} />
          <p className="font-headline text-headline-sm font-bold text-primary">{t("dashboardUi.houses.emptyTitle")}</p>
          <p className="max-w-md font-body text-body-md text-on-surface-variant">{t("dashboardUi.houses.emptyHint")}</p>
        </div>
      ) : (
        <ul className="mt-8 flex flex-col gap-3">
          {list.map((h, index) => {
            const isActive = h.id === activeHouseId;
            const summaryQ = houseSummaryQueries[index];
            const canEditOpeningBalance = h.role === "owner" || h.role === "admin";
            const canEditHouseName = h.role === "owner" || h.role === "admin";
            const canDeleteHouse = h.role === "owner";
            const editingFinancialProfile = financialProfileEditHouseId === h.id;
            const editingName = nameEditHouseId === h.id;
            return (
              <li
                key={h.id}
                className={cn(
                  "flex flex-col gap-3 rounded-2xl p-5 outline outline-2 sm:flex-row sm:items-start sm:justify-between",
                  isActive
                    ? "bg-primary/8 outline-primary/40 ring-1 ring-primary/20"
                    : "bg-surface-container-low outline-outline-variant/15",
                )}
              >
                <div className="min-w-0 flex-1">
                  {editingName ? (
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
                      <div className="min-w-0 flex-1">
                        <label className="text-label-md text-on-surface-variant" htmlFor={`house-name-${h.id}`}>
                          {t("dashboardUi.houses.nameLabel")}
                        </label>
                        <input
                          id={`house-name-${h.id}`}
                          value={nameEditValue}
                          onChange={(e) => setNameEditValue(e.target.value)}
                          className="mt-1 w-full rounded-xl border border-outline-variant/30 bg-surface px-4 py-3 font-body text-body-md text-on-surface outline-none focus:border-primary"
                          autoComplete="off"
                        />
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          type="button"
                          size="sm"
                          disabled={updateNameMutation.isPending || !nameEditValue.trim()}
                          onClick={() => {
                            const n = nameEditValue.trim();
                            if (!n) return;
                            updateNameMutation.mutate({ houseId: h.id, nextName: n });
                          }}
                        >
                          {t("dashboardUi.houses.editNameSave")}
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setNameEditHouseId(null)}
                        >
                          {t("dashboardUi.houses.editNameCancel")}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <p className="font-headline font-bold text-on-surface">{h.name}</p>
                  )}
                  <p className="text-label-md text-on-surface-variant">
                    {t("dashboardUi.houses.yourRole")}: {h.role}
                  </p>
                  <HouseFinancialProfileSection
                    houseId={h.id}
                    role={h.role}
                    isEditing={editingFinancialProfile}
                    onClose={() => setFinancialProfileEditHouseId(null)}
                  />
                  <div className="mt-3 border-t border-outline-variant/15 pt-3">
                    {summaryQ?.isLoading ? (
                      <div className="space-y-2" aria-busy>
                        <div className="h-3 w-40 animate-pulse rounded bg-on-surface-variant/15" />
                        <div className="h-3 w-56 animate-pulse rounded bg-on-surface-variant/10" />
                        <div className="h-1.5 max-w-[10rem] animate-pulse rounded-full bg-on-surface-variant/10" />
                      </div>
                    ) : summaryQ?.isError || !summaryQ?.data ? (
                      <p className="text-body-sm text-on-surface-variant">{t("dashboardUi.houses.cardSummaryError")}</p>
                    ) : (
                      (() => {
                        const g = resolveBudgetGlance(summaryQ.data);
                        const fmt = (minor: number) => formatMinorAsCurrency(minor, g.currency, locale);
                        const statusLabel =
                          g.kind === "within"
                            ? t("dashboardUi.houses.cardStatusWithin")
                            : g.kind === "tight"
                              ? t("dashboardUi.houses.cardStatusTight")
                              : g.kind === "over"
                                ? t("dashboardUi.houses.cardStatusOver")
                                : t("dashboardUi.houses.cardStatusNeutral");
                        const statusClass =
                          g.kind === "within"
                            ? "bg-secondary/15 text-secondary"
                            : g.kind === "tight"
                              ? "bg-amber-500/15 text-amber-800 dark:text-amber-200"
                              : g.kind === "over"
                                ? "bg-error/10 text-error"
                                : "bg-on-surface-variant/10 text-on-surface-variant";
                        const barClass =
                          g.kind === "within"
                            ? "bg-secondary"
                            : g.kind === "tight"
                              ? "bg-amber-500"
                              : g.kind === "over"
                                ? "bg-error"
                                : "bg-on-surface-variant/30";
                        const barWidth =
                          g.usagePercent != null ? Math.min(100, Math.max(0, g.usagePercent)) : 0;
                        return (
                          <div className="space-y-2">
                            <p className="text-[10px] font-label uppercase tracking-widest text-on-surface-variant/80">
                              {t("dashboardUi.houses.cardSummaryPeriod", { yearMonth })}
                            </p>
                            <div className="flex flex-wrap gap-x-5 gap-y-1 font-body text-body-sm text-on-surface">
                              <span>
                                <span className="text-on-surface-variant">{t("dashboardUi.houses.cardPlannedShort")}: </span>
                                <span className="font-semibold tabular-nums">{fmt(g.plannedMinor)}</span>
                              </span>
                              <span>
                                <span className="text-on-surface-variant">{t("dashboardUi.houses.cardSpentShort")}: </span>
                                <span className="font-semibold tabular-nums">{fmt(g.spentMinor)}</span>
                              </span>
                              {g.usagePercent != null ? (
                                <span>
                                  <span className="text-on-surface-variant">{t("dashboardUi.houses.cardUsageShort")}: </span>
                                  <span className="font-semibold tabular-nums">{Math.round(g.usagePercent)}%</span>
                                </span>
                              ) : null}
                            </div>
                            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
                              <span
                                className={cn(
                                  "inline-flex w-fit max-w-full rounded-full px-2.5 py-1 text-[11px] font-label font-semibold uppercase tracking-wide",
                                  statusClass,
                                )}
                              >
                                {statusLabel}
                              </span>
                              {g.usagePercent != null ? (
                                <div
                                  className="h-1.5 flex-1 overflow-hidden rounded-full bg-on-surface-variant/15 sm:max-w-[140px]"
                                  role="presentation"
                                >
                                  <div
                                    className={cn("h-full rounded-full transition-[width]", barClass)}
                                    style={{ width: `${barWidth}%` }}
                                  />
                                </div>
                              ) : null}
                            </div>
                          </div>
                        );
                      })()
                    )}
                  </div>
                </div>
                <div className="flex w-full shrink-0 flex-col gap-2 sm:w-auto sm:items-end sm:pt-0">
                  {canEditHouseName && !editingName ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="w-full sm:w-auto"
                      onClick={() => {
                        setNameEditHouseId(h.id);
                        setNameEditValue(h.name);
                        setFinancialProfileEditHouseId(null);
                      }}
                    >
                      {t("dashboardUi.houses.editNameCta")}
                    </Button>
                  ) : null}
                  {canDeleteHouse ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="w-full border-error/30 text-error hover:bg-error/10 sm:w-auto"
                      disabled={deleteHouseMutation.isPending}
                      onClick={() => {
                        if (window.confirm(t("dashboardUi.houses.deleteConfirm"))) {
                          deleteHouseMutation.mutate(h.id);
                        }
                      }}
                    >
                      {t("dashboardUi.houses.deleteCta")}
                    </Button>
                  ) : null}
                  {canEditOpeningBalance && !editingFinancialProfile ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="w-full sm:w-auto"
                      onClick={() => {
                        setFinancialProfileEditHouseId(h.id);
                        setNameEditHouseId(null);
                      }}
                    >
                      {t("dashboardUi.houses.financialProfileEdit")}
                    </Button>
                  ) : null}
                  <Button
                    type="button"
                    variant={isActive ? "default" : "outline"}
                    size="sm"
                    className="w-full sm:w-auto"
                    onClick={() => {
                      setActiveHouseId(h.id);
                      toast.success(t("dashboardUi.houses.setActiveSuccess"));
                    }}
                  >
                    {t("dashboardUi.houses.setActive")}
                  </Button>
                  <Button type="button" variant="outline" size="sm" className="w-full sm:w-auto" asChild>
                    <Link
                      to={dashboardPaths.houseMembers(h.id)}
                      {...(index === 0 ? { "data-tour": "houses-card-members" } : {})}
                    >
                      {t("dashboardUi.houses.membersLink")}
                    </Link>
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
